import React, { useState, useEffect } from 'react';
import MessageBox from './MessageBox';
import { INTERNET_ERROR_MESSAGE } from '../utils/internetError';
const API_BASE_URL = window.API_BASE_URL;

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Track mouse movement for parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 15 - 7.5,
        y: (e.clientY / window.innerHeight) * 15 - 7.5
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!username || !password) {
      setError('Please enter both username and password');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const raw = await response.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
        setError(
          offline
            ? INTERNET_ERROR_MESSAGE
            : response.status === 502 || response.status === 504
              ? 'Server is restarting or overloaded. Wait a few seconds and try again.'
              : 'Could not reach the login service. Please try again.',
        );
        setLoading(false);
        return;
      }

      if (!response.ok) {
        setError(data.internetError ? INTERNET_ERROR_MESSAGE : (data.message || 'Login failed'));
        setLoading(false);
        return;
      }

      localStorage.setItem('authToken', data.token);
      
      onLogin({
        id: data.user.id,
        username: data.user.username,
        name: data.user.name,
        email: data.user.email,
        role: data.user.role,
      });
    } catch (err) {
      setError(err && err.internetError
        ? INTERNET_ERROR_MESSAGE
        : 'Connection error. Please check if backend server is running.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-15px); } }
        @keyframes slideIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes gradient { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        .input-wrapper { position: relative; }
        .input-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; transition: color 0.3s; }
        .input-wrapper:focus-within .input-icon { color: #3B82F6; }
        @media (max-width: 968px) { .illustration-side { display: none !important; } }
      `}</style>
      
      <div style={{
        minHeight: '100vh',
        maxHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        backgroundSize: '200% 200%',
        animation: 'gradient 15s ease infinite',
        padding: '15px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated gradient orbs */}
        <div style={{ position: 'absolute', top: '10%', right: '5%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(59, 130, 246, 0.25), transparent 70%)', borderRadius: '50%', animation: 'float 20s ease-in-out infinite', pointerEvents: 'none', filter: 'blur(50px)' }} />
        <div style={{ position: 'absolute', bottom: '10%', left: '5%', width: '350px', height: '350px', background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2), transparent 70%)', borderRadius: '50%', animation: 'float 25s ease-in-out infinite reverse', pointerEvents: 'none', filter: 'blur(50px)' }} />
        
        {/* Main Container */}
        <div style={{
          display: 'flex',
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: '100%',
          maxWidth: '950px',
          maxHeight: '90vh',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1,
          animation: 'slideIn 0.7s ease-out'
        }}>
          {/* Illustration Side */}
          <div className="illustration-side" style={{
            flex: '0 0 45%',
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '30px',
            position: 'relative'
          }}>
            {/* Illustration */}
            <div style={{
              animation: 'float 5s ease-in-out infinite',
              transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)`,
              transition: 'transform 0.1s ease-out'
            }}>
              <img 
                src="https://illustrations.popsy.co/amber/student-going-to-school.svg"
                alt="Education"
                style={{
                  width: '100%',
                  maxWidth: '320px',
                  height: 'auto',
                  filter: 'drop-shadow(0 15px 30px rgba(0, 0, 0, 0.2))'
                }}
              />
            </div>
            
            {/* Text */}
            <div style={{ marginTop: '25px', textAlign: 'center' }}>
              <h3 style={{ fontSize: '22px', fontWeight: '800', color: 'white', margin: '0 0 10px 0', textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }}>
                Grade Smarter, Not Harder
              </h3>
              <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontWeight: '500' }}>
                AI-powered grading that saves time
              </p>
              
              {/* Badges */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {[
                  { icon: 'fa-bolt', text: 'Fast' },
                  { icon: 'fa-check-circle', text: 'Accurate' },
                  { icon: 'fa-shield-alt', text: 'Secure' }
                ].map((badge, i) => (
                  <div key={i} style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    padding: '6px 12px',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    animation: `fadeIn 0.6s ease-out ${i * 0.1}s both`
                  }}>
                    <i className={`fas ${badge.icon}`} style={{ color: 'white', fontSize: '12px' }}></i>
                    <span style={{ color: 'white', fontSize: '12px', fontWeight: '600' }}>{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form Side */}
          <div style={{
            flex: '1',
            padding: '40px 35px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <img 
                src={`${process.env.PUBLIC_URL}/Logo-FastGrader.png`} 
                alt="FastGrader Logo" 
                style={{ height: '60px', width: 'auto', margin: '0 auto 18px', display: 'block' }}
              />
              <h2 style={{
                fontSize: '24px',
                fontWeight: '800',
                background: 'linear-gradient(135deg, #1e40af, #3B82F6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                margin: '0 0 6px 0'
              }}>
                Welcome Back!
              </h2>
              <p style={{ fontSize: '14px', color: '#64748b', margin: 0, fontWeight: '500' }}>
                Sign in to continue to FastGrader
              </p>
            </div>
            
            <form onSubmit={handleSubmit}>
              {error && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px 14px',
                  background: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: '8px',
                  color: '#c33',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>⚠️</span> {error}
                </div>
              )}

              <div style={{ marginBottom: '18px' }}>
                <label htmlFor="username" style={{ display: 'block', color: '#334155', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                  Username
                </label>
                <div className="input-wrapper">
                  <i className="fas fa-user input-icon" style={{ fontSize: '14px' }}></i>
                  <input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    style={{
                      width: '100%',
                      padding: '13px 16px 13px 42px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      fontSize: '14px',
                      transition: 'all 0.3s',
                      outline: 'none',
                      background: '#f8fafc'
                    }}
                    placeholder="Enter your username"
                    onFocus={(e) => { e.target.style.borderColor = '#3B82F6'; e.target.style.background = 'white'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="password" style={{ display: 'block', color: '#334155', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                  Password
                </label>
                <div className="input-wrapper">
                  <i className="fas fa-lock input-icon" style={{ fontSize: '14px' }}></i>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    style={{
                      width: '100%',
                      padding: '13px 16px 13px 42px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      fontSize: '14px',
                      transition: 'all 0.3s',
                      outline: 'none',
                      background: '#f8fafc'
                    }}
                    placeholder="Enter your password"
                    onFocus={(e) => { e.target.style.borderColor = '#3B82F6'; e.target.style.background = 'white'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  background: loading ? '#94a3b8' : 'linear-gradient(135deg, #2563EB, #3B82F6)',
                  color: 'white',
                  padding: '14px',
                  borderRadius: '10px',
                  border: 'none',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: loading ? 'none' : '0 6px 16px rgba(37, 99, 235, 0.4)',
                  opacity: loading ? 0.7 : 1
                }}
                onMouseOver={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 20px rgba(37, 99, 235, 0.5)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 6px 16px rgba(37, 99, 235, 0.4)';
                  }
                }}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <i className="fas fa-spinner fa-spin"></i> Signing In...
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <i className="fas fa-sign-in-alt"></i> SIGN IN
                  </span>
                )}
              </button>
            </form>

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button 
                type="button"
                onClick={() => setMessage({ show: true, text: 'Please contact your administrator for password reset.', type: 'info' })}
                style={{
                  fontSize: '13px',
                  color: '#3B82F6',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'all 0.3s',
                  padding: '6px 12px',
                  borderRadius: '6px'
                }}
                onMouseOver={(e) => { e.target.style.background = 'rgba(59, 130, 246, 0.1)'; }}
                onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
              >
                <i className="fas fa-key" style={{ marginRight: '6px' }}></i>
                Forgot password?
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <MessageBox 
        message={message} 
        onClose={() => setMessage({ show: false, text: '', type: 'info' })} 
      />
    </>
  );
};

export default Login;
